import { useState } from 'react'
import { CandidateWarning } from '../components/CandidateWarning'
import { SearchBar } from '../components/SearchBar'
import { EntryCard } from '../components/EntryCard'
import { SiteFooter } from '../components/SiteFooter'
import { useSearch } from '../hooks/useSearch'
import { useBatchVotes } from '../hooks/useVotes'

export function SearchPage() {
  const [query, setQuery] = useState('')
  const { entries, total, loading, error, page, totalPages, setPage } = useSearch(query)
  const { counts: voteCounts, myVotes, castVote } = useBatchVotes(entries.map(e => e.entry_id))

  return (
    <div className="min-h-screen flex flex-col">
      {/* Candidate warning banner */}
      <CandidateWarning />

      {/* Site header */}
      <header className="bg-tamang-700 text-white px-4 py-6 shadow-md">
        <div className="max-w-5xl mx-auto">
          <h1 className="text-2xl font-bold tracking-tight">
            Tamang Dictionary
          </h1>
          <p className="text-sm opacity-70 mt-1">
            Eastern Tamang (taj) · Tamang–Nepali–English · Machine-extracted candidates
          </p>
          <div className="mt-4">
            <SearchBar value={query} onChange={setQuery} autoFocus />
          </div>
        </div>
      </header>

      {/* Results area */}
      <main className="flex-1 max-w-5xl mx-auto w-full px-4 py-6">

        {/* Count line */}
        <div className="flex items-center justify-between mb-4 text-sm text-stone-500">
          {loading ? (
            <span className="animate-pulse">Searching…</span>
          ) : error ? (
            <span className="text-rose-600">Error: {error}</span>
          ) : (
            <span>
              {query
                ? `${total.toLocaleString()} result${total !== 1 ? 's' : ''} for "${query}"`
                : `Showing top candidates by frequency`}
            </span>
          )}
          {totalPages > 1 && (
            <span className="text-stone-400">
              Page {page + 1} / {totalPages}
            </span>
          )}
        </div>

        {/* Loading skeleton */}
        {loading && entries.length === 0 && (
          <div className="grid sm:grid-cols-2 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="dict-card p-5 space-y-3 animate-pulse">
                <div className="h-8 w-2/3 bg-stone-200 rounded" />
                <div className="h-5 w-1/2 bg-stone-100 rounded" />
                <div className="h-4 w-3/4 bg-stone-100 rounded" />
              </div>
            ))}
          </div>
        )}

        {/* Results grid */}
        {!loading && entries.length > 0 && (
          <div className="grid sm:grid-cols-2 gap-4">
            {entries.map(e => (
              <EntryCard
                key={e.entry_id}
                entry={e}
                counts={voteCounts[e.entry_id]}
                myVote={myVotes[e.entry_id] ?? null}
                castVote={castVote}
              />
            ))}
          </div>
        )}

        {/* Empty state */}
        {!loading && entries.length === 0 && !error && (
          <div className="text-center py-20 text-stone-400">
            <p className="text-4xl mb-4">🔍</p>
            <p className="text-lg font-medium">No entries found</p>
            <p className="text-sm mt-1">
              Try searching in Tamang script, Nepali, or English.
            </p>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-8">
            <button
              className="btn-secondary"
              disabled={page === 0}
              onClick={() => setPage(page - 1)}
            >
              ← Previous
            </button>
            <span className="px-4 text-sm text-stone-500">
              {page + 1} / {totalPages}
            </span>
            <button
              className="btn-secondary"
              disabled={page >= totalPages - 1}
              onClick={() => setPage(page + 1)}
            >
              Next →
            </button>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  )
}
